import { useEffect, useRef, type CSSProperties, type FC } from 'react';
import { V4 } from '../tokens';
import { Z } from '../zIndex';
import { ROLE_CTA } from '@utils/featureRollout/strings';

// Modal contextual de invitación (primera vez que un comercio elegible entra al menú).
// Refuerza la visibilidad de la nueva versión sin ser bloqueante: se puede cerrar, probar
// la versión, o marcar "no volver a preguntar". Reusa el lenguaje visual de PromoModal /
// VersionPickerCard (overlay + card V4 con sombra), 100% inline styles con tokens.
//
// CTA POR ROL (Item E#1): root deja activa la versión nueva (switch durable); no-root echa un
// vistazo (vista previa personal). Ya NO se bloquea al no-root tras el click.

export interface FeatureInvitationModalProps {
  isRoot: boolean;
  // ROOT: dispara el switch DURABLE a treatment (fire-and-forget). Lo provee el consumidor.
  onTry: () => void;
  // NO-ROOT: enciende la vista previa per-usuario (no toca el comercio). Si no se provee, el
  // no-root cae a onTry (compatibilidad).
  onPreview?: () => void;
  // Cierra el modal sin persistir nada (X, backdrop, Escape). NO snooze ni opt_out: la cadencia
  // server-side decidirá si reaparece. Cierre suave, no compromiso.
  onDismiss: () => void;
  // "Recordármelo más tarde": snooze (PUT snooze_days). El backend suprime hasta now+días.
  onRemindLater: () => void;
  // "No volver a preguntar": opt_out (PUT behavior='opt_out'). El backend la vuelve sticky.
  onNeverAsk: () => void;
  // Telemetría: la invitación se mostró (una vez por montaje).
  onShown?: () => void;
  title?: string;
  description?: string;
}

const SparkIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" />
  </svg>
);

const CloseIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
  </svg>
);

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(17,26,34,.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: Z.overlayPrompt,
  fontFamily: 'inherit',
};

const cardStyle: CSSProperties = {
  background: V4.white,
  borderRadius: 14,
  boxShadow: '0 24px 60px rgba(17,26,34,.32)',
  border: `1px solid ${V4.grayLight}`,
  width: '100%',
  maxWidth: 420,
  overflow: 'hidden',
  position: 'relative',
};

const primaryButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  background: V4.secondary,
  color: V4.white,
  border: 0,
  borderRadius: 9,
  padding: '11px 16px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 700,
  width: '100%',
};

const linkButton: CSSProperties = {
  background: 'transparent',
  color: V4.grayUltraDark,
  border: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 600,
  padding: '8px 6px',
};

export const FeatureInvitationModal: FC<FeatureInvitationModalProps> = ({
  isRoot,
  onTry,
  onPreview,
  onDismiss,
  onRemindLater,
  onNeverAsk,
  onShown,
  title = 'Prueba la nueva versión del menú',
  description = 'Rediseñamos esta sección para que gestiones tu menú más rápido. Pruébala cuando quieras y regresa a la anterior si lo prefieres.',
}) => {
  const shownRef = useRef(false);

  // Telemetría invitation_shown: una vez al montar. Guard con ref para no re-disparar.
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    onShown?.();
  }, [onShown]);

  // Cerrar con Escape (no bloqueante).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  // CTA por rol EN RENDER: root deja activa la nueva (durable); no-root echa un vistazo (preview).
  const handleCta = () => {
    if (isRoot) {
      onTry();
      return;
    }
    if (onPreview) onPreview();
    else onTry();
  };

  return (
    <div
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        // Click en el backdrop = cerrar (no bloqueante). Click dentro de la card no propaga.
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div style={cardStyle}>
        <button
          type="button"
          aria-label="Cerrar"
          onClick={onDismiss}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 30,
            height: 30,
            borderRadius: 8,
            border: 0,
            background: V4.grayBlueLight,
            color: V4.grayUltraDark,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {CloseIcon}
        </button>

        <div style={{ padding: '26px 22px 18px', textAlign: 'center' }}>
          <span
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: V4.grayBlueLight,
              color: V4.secondary,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 14,
            }}
          >
            {SparkIcon}
          </span>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: V4.primary, lineHeight: 1.25 }}>{title}</h2>
          <p
            style={{
              margin: '8px 0 0',
              fontSize: 13.5,
              color: V4.grayUltraDark,
              lineHeight: 1.5,
            }}
          >
            {description}
          </p>
        </div>

        <div style={{ padding: '0 22px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button type="button" style={primaryButton} onClick={handleCta}>
            {SparkIcon}
            {isRoot ? ROLE_CTA.rootTry : ROLE_CTA.operatorPreview}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <button type="button" style={linkButton} onClick={onRemindLater}>
              Recordármelo más tarde
            </button>
            <span style={{ color: V4.grayLight }}>·</span>
            <button type="button" style={linkButton} onClick={onNeverAsk}>
              No volver a preguntar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureInvitationModal;
